import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Copy,
  CreditCard,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { cardTerminalApi, storefrontAdminApi } from '@/services/api';
import { getApiErrorMessage } from '@/utils/apiError';

/**
 * Public Store → Teya tab (mirrors StripeSettingsTab).
 * Configures online hosted checkout for the shop — not the POS card machine.
 */
export default function TeyaStoreSettingsTab({ embedded = false }: { embedded?: boolean }) {
  const qc = useQueryClient();
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [testing, setTesting] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['teyaSettings'],
    queryFn: async () => (await cardTerminalApi.getTeyaSettings()).data.message,
  });

  const { data: storeSettings } = useQuery({
    queryKey: ['storefrontAdminSettings'],
    queryFn: async () => (await storefrontAdminApi.getSettings()).data.message,
  });

  const [form, setForm] = useState({
    enabled: false,
    mode: 'Sandbox' as 'Sandbox' | 'Live',
    currency: 'GBP',
    client_id: '',
    client_secret: '',
    store_id: '',
  });

  useEffect(() => {
    if (!settings) return;
    const storeOn = Number(storeSettings?.pay_online_teya) === 1;
    const credsOn = Number(settings.enabled) === 1 || settings.enabled === true;
    const hostedOn = Number(settings.enable_hosted_checkout) !== 0;
    setForm({
      // Shop toggle = storefront flag (preferred) or credentials ready for hosted checkout
      enabled: storeOn || (credsOn && hostedOn),
      mode: (settings.mode as 'Sandbox' | 'Live') || 'Sandbox',
      currency: settings.currency || 'GBP',
      client_id: settings.client_id || '',
      client_secret: '',
      store_id: settings.store_id || '',
    });
  }, [settings, storeSettings?.pay_online_teya]);

  const saveMutation = useMutation({
    mutationFn: async (payload: typeof form & { _toggleOnly?: boolean }) => {
      // Embedded under Card payment: selection already chose Teya — keep it on while configuring
      const enabled = embedded ? true : Boolean(payload.enabled);
      await cardTerminalApi.saveTeyaSettings({
        enabled: enabled ? 1 : 0,
        mode: payload.mode,
        currency: payload.currency,
        client_id: payload.client_id,
        client_secret: payload.client_secret || undefined,
        store_id: payload.store_id,
        // Keep existing terminal fields; only flip hosted checkout for the shop
        enable_hosted_checkout: enabled ? 1 : 0,
        sync_storefront: 1,
        // Do not wipe terminal / pay-by-link flags when saving from store tab
        enable_pay_by_link: settings?.enable_pay_by_link !== 0 ? 1 : 0,
        terminal_id: settings?.terminal_id || undefined,
        epos_external_id: settings?.epos_external_id || undefined,
      });
      // Exclusive storefront flags (one gateway at a time)
      await storefrontAdminApi.saveSettings({
        ...(storeSettings || {}),
        pay_online_teya: enabled ? 1 : 0,
        pay_online_stripe: enabled ? 0 : storeSettings?.pay_online_stripe || 0,
        pay_online_dojo: enabled ? 0 : storeSettings?.pay_online_dojo || 0,
      });
    },
    onSuccess: (_d, variables) => {
      qc.invalidateQueries({ queryKey: ['teyaSettings'] });
      qc.invalidateQueries({ queryKey: ['storefrontAdminSettings'] });
      qc.invalidateQueries({ queryKey: ['cardTerminalConfig'] });
      setForm((f) => ({ ...f, client_secret: '' }));
      toast.success(
        variables._toggleOnly
          ? variables.enabled
            ? 'Teya shop payments enabled'
            : 'Teya shop payments disabled'
          : 'Teya shop settings saved',
      );
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const toggleEnabled = () => {
    if (saveMutation.isPending) return;
    const prev = form.enabled;
    const next = !prev;
    setForm((f) => ({ ...f, enabled: next }));
    saveMutation.mutate({ ...form, enabled: next, _toggleOnly: true }, {
      onError: () => setForm((f) => ({ ...f, enabled: prev })),
    });
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = (
        await cardTerminalApi.testTeya({
          mode: form.mode,
          client_id: form.client_id || undefined,
          client_secret: form.client_secret || undefined,
        })
      ).data.message;
      toast.success(res?.message || 'Connected to Teya');
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setTesting(false);
    }
  };

  const copyWebhook = () => {
    if (!settings?.webhook_url) return;
    void navigator.clipboard.writeText(settings.webhook_url);
    setCopiedWebhook(true);
    toast.success('Webhook URL copied');
    setTimeout(() => setCopiedWebhook(false), 2000);
  };

  const ready =
    form.enabled &&
    Boolean(form.client_id) &&
    (Boolean(settings?.has_client_secret) || Boolean(form.client_secret));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin text-teal-700 mr-2" />
        <span className="text-sm font-medium">Loading Teya configuration…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {!embedded && (
        <>
      <div className="bg-gradient-to-r from-slate-900 via-teal-950 to-slate-900 rounded-2xl p-6 text-white shadow-md">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider bg-teal-500/20 text-teal-200 border border-teal-400/30">
                Online checkout
              </span>
              <span
                className={`px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider border ${
                  form.enabled
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40'
                    : 'bg-gray-500/20 text-gray-400 border-gray-400/30'
                }`}
              >
                {form.enabled ? (form.mode === 'Live' ? 'Live Mode' : 'Sandbox Mode') : 'Disabled'}
              </span>
            </div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-teal-300" />
              Teya Shop Integration
            </h2>
            <p className="text-xs text-teal-100/80 mt-1 max-w-xl">
              Accept card payments on your public store via Teya Hosted Checkout. POS card machines and
              Pay by Link stay under Settings → Card machines.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.enabled}
            disabled={saveMutation.isPending}
            onClick={toggleEnabled}
            className="inline-flex items-center gap-3 shrink-0 disabled:opacity-60"
          >
            <span
              className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                form.enabled ? 'bg-teal-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  form.enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </span>
            <span className="text-xs font-bold text-white">{form.enabled ? 'Enabled' : 'Disabled'}</span>
          </button>
        </div>
      </div>

      <div
        className={`flex items-start gap-2 rounded-xl px-3 py-2.5 text-sm ${
          ready ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'
        }`}
      >
        {ready ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
        {ready
          ? 'Customers can choose Pay online (Teya) at checkout.'
          : 'Enable Teya and save Client ID + Secret so the store can offer card checkout.'}
      </div>
        </>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          saveMutation.mutate(form);
        }}
        className="space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card p-5 bg-white border border-gray-200 shadow-sm space-y-3">
            <label className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-amber-500" />
              Environment
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(['Sandbox', 'Live'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setForm({ ...form, mode: m })}
                  className={`p-3 rounded-xl border text-left text-sm ${
                    form.mode === m
                      ? 'border-teal-600 bg-teal-50 ring-2 ring-teal-500/20'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold">{m === 'Sandbox' ? 'Sandbox' : 'Live'}</div>
                  <p className="text-[10px] text-gray-500 mt-1">
                    {m === 'Sandbox' ? 'Test with staging credentials' : 'Real charges'}
                  </p>
                </button>
              ))}
            </div>
          </div>
          <div className="card p-5 bg-white border border-gray-200 shadow-sm space-y-3">
            <label className="text-xs font-bold text-gray-900 uppercase tracking-wider">Currency</label>
            <select
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm"
            >
              <option value="GBP">GBP — British Pound</option>
              <option value="EUR">EUR — Euro</option>
              <option value="USD">USD — US Dollar</option>
            </select>
            <p className="text-[11px] text-slate-500">
              Need a physical card machine? Configure it in{' '}
              <Link to="/settings?tab=card_machines" className="underline font-medium text-slate-800">
                Settings → Card machines
              </Link>
              .
            </p>
          </div>
        </div>

        <div className="card p-5 bg-white border border-gray-200 shadow-sm space-y-4">
          <h3 className="text-sm font-bold text-slate-900">API credentials</h3>
          <p className="text-xs text-slate-500">
            From{' '}
            <a
              href={form.mode === 'Live' ? 'https://business.teya.com' : 'https://business.teya.xyz'}
              target="_blank"
              rel="noreferrer"
              className="underline inline-flex items-center gap-1"
            >
              Teya Business Portal <ExternalLink className="w-3 h-3" />
            </a>{' '}
            → Integrations (ecommerce / Online Payments).
          </p>
          <label className="block text-sm space-y-1">
            <span className="text-slate-600">Client ID</span>
            <input
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
              autoComplete="off"
              required={form.enabled}
            />
          </label>
          <label className="block text-sm space-y-1">
            <span className="text-slate-600">
              Client Secret{' '}
              {settings?.has_client_secret ? <span className="text-emerald-600">(saved)</span> : null}
            </span>
            <div className="relative">
              <input
                type={showSecret ? 'text' : 'password'}
                value={form.client_secret}
                onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 pr-10 font-mono text-xs"
                placeholder={settings?.has_client_secret ? 'Leave blank to keep current secret' : 'Paste secret'}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                onClick={() => setShowSecret((v) => !v)}
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </label>
          <label className="block text-sm space-y-1">
            <span className="text-slate-600">Store ID (optional)</span>
            <input
              value={form.store_id}
              onChange={(e) => setForm({ ...form, store_id: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
            />
          </label>

          {settings?.webhook_url ? (
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Webhook URL
              </div>
              <div className="flex items-center gap-2">
                <code className="text-[11px] text-slate-700 break-all flex-1">{settings.webhook_url}</code>
                <button
                  type="button"
                  onClick={copyWebhook}
                  className="p-2 rounded-lg hover:bg-white border border-slate-200"
                >
                  {copiedWebhook ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Add this in Teya Integrations and listen for payment success events.
              </p>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void handleTest()}
            disabled={testing}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Test Teya connection
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-teal-700 text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60"
          >
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Save Teya configuration
          </button>
        </div>
      </form>
    </div>
  );
}
